"use client"

import { useCallback, useEffect } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"


// router.replace does NOT update window.location synchronously in the App Router (the navigation
// commits in a transition), so a second setter fired in the same handler used to read the OLD
// query string and silently wipe the first one's change. "Edit Script" sets the tab, the editor and
// the script id back to back and lost two of the three. Writes made in the same burst now build on
// the query string the previous write produced, until the real URL catches up.
let pendingSearch: string | null = null
let pendingTimer: ReturnType<typeof setTimeout> | null = null

function currentSearchParams(): URLSearchParams {
  if (typeof window === "undefined") return new URLSearchParams()
  return new URLSearchParams(pendingSearch ?? window.location.search)
}

function rememberPendingSearch(query: string) {
  pendingSearch = query
  if (pendingTimer) clearTimeout(pendingTimer)
  pendingTimer = setTimeout(() => {
    pendingSearch = null
    pendingTimer = null
  }, 1000)
}

interface UseUrlStateOptions<T extends string> {
  defaultValue: T
  /** Rejects a malformed/unknown value read from the URL — falls back to defaultValue instead. */
  validate?: (value: string) => boolean
}

/**
 * Keeps one piece of state in sync with a URL query param, in both
 * directions — so a page refresh lands back on the same content instead of
 * bouncing to the default view. Extracted from the pattern already proven at
 * app/page.tsx's `viewMode` (see the comment there: "the actual fix for
 * 'refresh inside Editors Hub / a tab like Devices bounces to home'").
 *
 * Always uses router.replace with scroll:false, never router.push — every
 * existing hand-rolled version of this pattern in the app (viewMode here,
 * Creator Hub's syncUrl, the chat session param) does the same, so this
 * never spams browser history just because state changed.
 */
export function useUrlState<T extends string>(
  key: string,
  { defaultValue, validate }: UseUrlStateOptions<T>
): [T, (next: T) => void] {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const raw = searchParams.get(key)
  const value = raw && (!validate || validate(raw)) ? (raw as T) : defaultValue

  const setValue = useCallback(
    (next: T) => {
      // Read the LIVE URL (window.location), not the searchParams hook value —
      // when several of these setters fire synchronously in one handler (e.g.
      // selecting a script sets which script + which editor + which tab all
      // in one click), each call must build on what the PREVIOUS call just
      // wrote, not on the snapshot from before the click. router.replace
      // updates window.location synchronously (history.replaceState) even
      // though the React re-render that produces a new searchParams value is
      // deferred, so a closed-over searchParams goes stale after the first
      // call in the chain and the later calls silently clobber it back out.
      const params = currentSearchParams()
      const current = params.get(key)
      const nextParamValue = next === defaultValue ? null : next
      if (current === nextParamValue) return
      if (nextParamValue === null) params.delete(key)
      else params.set(key, nextParamValue)
      const query = params.toString()
      rememberPendingSearch(query)
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
    },
    [key, defaultValue, pathname, router]
  )

  return [value, setValue]
}

/** Same idea as useUrlState, but for setting/clearing a param without owning
 * a matched "default" round-trip — useful for opaque ids (e.g. `?post=abc`)
 * where "no param" and "some value" are the only two states. */
export function useUrlParamSetter(key: string) {
  const router = useRouter()
  const pathname = usePathname()

  return useCallback(
    (next: string | null) => {
      // Same live-URL reasoning as useUrlState's setValue above — avoids
      // clobbering a sibling setter's change made earlier in the same tick.
      const params = currentSearchParams()
      const current = params.get(key)
      if (current === next) return
      if (next === null) params.delete(key)
      else params.set(key, next)
      const query = params.toString()
      rememberPendingSearch(query)
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
    },
    [key, pathname, router]
  )
}

/** Reads a param once at mount time (lazy-init style) — for building a
 * useState initializer, e.g. restoring selectedPost from sessionStorage
 * keyed by the URL param. Not reactive; pair with useUrlParamSetter for the
 * write side, same split app/page.tsx already uses for viewMode (read-side
 * lazy init + a separate write-side effect). */
export function readUrlParamOnce(key: string): string | null {
  if (typeof window === "undefined") return null
  return new URLSearchParams(window.location.search).get(key)
}
